import React, { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { useLang } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";
import { toast } from "sonner";
import { ClipboardList, AlertCircle, CheckCircle2 } from "lucide-react";

export default function MemberPortal() {
  const { t, lang } = useLang();
  const { user, ready } = useAuth();
  const [surveys, setSurveys] = useState([]);
  const [choices, setChoices] = useState({});

  useEffect(() => {
    if (user && user.status === "approved") {
      api.get("/surveys").then((r) => setSurveys(r.data)).catch(() => {});
    }
  }, [user]);

  if (!ready) return <div className="p-12 text-center text-slate-500">{t("common.loading")}</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === "admin") return <Navigate to="/admin" replace />;

  if (user.status !== "approved") {
    return (
      <div className="min-h-[60vh] flex items-center justify-center px-4 py-12" data-testid="portal-pending">
        <div className="bg-pink-50 border border-pink-200 rounded-2xl p-8 max-w-lg text-center">
          <AlertCircle size={36} className="mx-auto text-[#d81b7a]" />
          <h1 className="font-display text-2xl font-bold text-slate-900 mt-4">{t("portal.pending")}</h1>
          <p className="text-slate-600 mt-3">{t("portal.pendingMsg")}</p>
        </div>
      </div>
    );
  }

  const vote = async (id) => {
    const c = choices[id];
    if (c == null) return;
    try {
      await api.post(`/surveys/${id}/vote`, { choice: c });
      toast.success("Vote submitted");
      const r = await api.get("/surveys");
      setSurveys(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    }
  };

  return (
    <div data-testid="portal-page">
      <section className="cupe-gradient text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <h1 className="font-display text-3xl md:text-4xl font-bold">{t("portal.title")}</h1>
          <p className="mt-2 opacity-90">{t("portal.welcome")}, {user.name}</p>
        </div>
      </section>

      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <h2 className="font-display text-2xl font-bold text-slate-900 mb-2 flex items-center gap-2"><ClipboardList /> {t("portal.surveysTitle")}</h2>
        <div className="h-1 w-12 cupe-gradient rounded-full mb-8" />

        <div className="space-y-5">
          {surveys.map((s) => {
            const total = (s.tally || []).reduce((a, b) => a + b, 0) || 1;
            const opts = lang === "fr" ? s.options_fr : s.options_en;
            return (
              <div key={s.id} className="bg-white border border-slate-200 rounded-2xl p-6" data-testid="survey-card">
                <h3 className="font-display font-bold text-lg text-slate-900">{lang === "fr" ? s.question_fr : s.question_en}</h3>
                {s.user_voted ? (
                  <div className="mt-4 space-y-2">
                    <div className="text-sm text-emerald-700 flex items-center gap-1"><CheckCircle2 size={14} /> {t("portal.voted")}</div>
                    <div className="mt-3 space-y-2">
                      {opts.map((o, i) => {
                        const pct = Math.round(((s.tally?.[i] || 0) / total) * 100);
                        return (
                          <div key={i}>
                            <div className="flex justify-between text-sm"><span className="text-slate-700">{o}</span><span className="text-slate-500">{pct}%</span></div>
                            <div className="h-2 bg-slate-100 rounded-full overflow-hidden"><div className="h-full cupe-gradient" style={{ width: `${pct}%` }} /></div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ) : (
                  <div className="mt-4 space-y-2">
                    {opts.map((o, i) => (
                      <label key={i} className="flex items-center gap-3 p-3 rounded-md border border-slate-200 hover:border-[#d81b7a] cursor-pointer">
                        <input type="radio" name={`s-${s.id}`} checked={choices[s.id] === i} onChange={() => setChoices({ ...choices, [s.id]: i })} className="accent-[#d81b7a]" />
                        <span>{o}</span>
                      </label>
                    ))}
                    <button onClick={() => vote(s.id)} disabled={choices[s.id] == null} className="mt-3 cupe-gradient text-white px-5 py-3 rounded-md font-semibold disabled:opacity-50 min-h-[48px]" data-testid={`vote-${s.id}`}>
                      {t("portal.voteBtn")}
                    </button>
                  </div>
                )}
              </div>
            );
          })}
          {surveys.length === 0 && <p className="text-slate-400">{t("portal.noSurveys")}</p>}
        </div>
      </section>
    </div>
  );
}
